import { mapActions, mapMutations } from 'vuex';
import iconsMixin from '@mixin/channelIcons.js';
import Accordion from '@svgIcons/accordion_open.svg';
import EditIcon from '@svgIcons/edit.svg';
import CheckIcon from '@svgIcons/check.svg';

import * as R from 'ramda';
import memoize from 'lodash/memoize';
import throttle from 'lodash/throttle';

import CheckBox from '@/components/Common/Checkbox';

import {
    notEq,
    isSubset,
    listIncludes,
    isValueExist,
    isEllipsisActive,
    CLASS_JOIN,
} from '@util';
import { isCustomMetric } from '@/utils/dashboard';
import escapeRegExp from 'lodash/escapeRegExp';

import { DIRECTIVE_CLAMP as _DIRECTIVE_CLAMP } from '@/styles/common.styled';
import * as S from './styles';
import { WIDGET_TYPES } from '@/utils/consts';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { refineRawText } from '@/utils/translator';

const DIRECTIVE_CLAMP = R.assocPath(
    ['directives', 0, 'value'],
    3,
    _DIRECTIVE_CLAMP
);

const InRange = (start, end, index) => {
    return index >= start && end >= index;
};

export default {
    name: 'PanelSecondPanel',
    props: {
        loopTarget: {
            type: Array,
            default() {
                return [];
            },
        },
        currentList: {
            type: Array,
            default() {
                return [];
            },
        },
        rawList: {
            type: Array,
            default() {
                return [];
            },
        },
        isItemDisabled: {
            type: Function,
            default: R.F,
        },
        isItemEditable: {
            type: Function,
            default: R.F,
        },
        hasCheckbox: Boolean,
        getCategory: {
            type: Function,
            default: R.always(''),
        },
        filterByCategory: {
            type: Function,
            default: R.identity,
        },
        hasCheckboxAll: {
            type: Boolean,
            default: true,
        },
        expandOnCheckAllClick: {
            type: Boolean,
            default: true,
        },
        hasCategoryCheckboxAll: {
            type: Boolean,
            default: null,
        },
        hideCategoryCheckbox: {
            type: Boolean,
            default: false,
        },
        searchText: String,
        translator: Function,
        indexKey: String,
        tooltipGenerator: {
            type: Function,
            default: R.always(''),
        },
        additionalTooltipGenerator: {
            type: Function,
            default: R.always(''),
        },
        isCheckAllDisabled: {
            type: Function,
            default: R.F,
        },
        panelLoading: {
            type: Boolean,
            default: false,
        },
        additionalIconGenerator: {
            type: Function,
            default: R.always(''),
        },
        disabledOnClick: Function,
        categoryNameGenerator: {
            type: Function,
            default: R.always(''),
        },
        customTextGenerator: Function,
        widgetType: String,
        rListUpdateKey: String,
        rListCompareFn: {
            type: Function,
            default: R.identity,
        },
        categoryCheckedResolver: {
            type: Function,
            default: R.always(null),
        },
        categoryIndeterminateResolver: {
            type: Function,
            default: null,
        },
        resolveAllChecked: {
            type: Function,
            default: null,
        },
        useEnhancedList: {
            type: Boolean,
            default: false,
        },
        autoOpenFirstCategory: {
            type: Boolean,
            default: true,
        },
        showEmptyCategories: {
            type: Boolean,
            default: false,
        },
        emptyCategoryMessage: {
            type: [Function, String],
            default: null,
        },
        handleCategoryToggle: {
            type: Function,
            default: null,
        },
        categoryEditHandler: {
            type: Function,
            default: null,
        },
        categoryContentRenderer: {
            type: Function,
            default: null,
        },
        reactiveListDebounceMs: {
            type: Number,
            default: 200,
        },
        categoryMapDebounceMs: {
            type: Number,
            default: 250,
        },
        unselectAllLabelKey: {
            type: String,
            default: 'COMMON.unselect_all',
        },
        defaultCategoryClosed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const visibleCnt = Math.floor(window.innerHeight / 30);

        return {
            visibleRange: [0, visibleCnt],
            visibleCnt,
            closedMap: {},
            searchedListClosedMap: {},
            visibles: new Set(),
            tootipVisibleMap: {},
            searchedList: Object.freeze([]),
            itemListCategoryMap: {},
            reactiveList: [],
        };
    },
    mounted() {
        this.$genSearchedList();
        this.$genItemListCategoryMap();
        this.$applyToReactiveList();
    },
    watch: {
        searchText: {
            handler() {
                this.searchedListClosedMap = {};
            },
        },
    },
    computed: {
        selectedUniqKeys() {
            return R.compose(
                R.pluck(this.indexKey),
                R.filter(R.prop('selected'))
            )(this.currentList);
        },
        effectiveHasCategoryCheckboxAll() {
            return this.hasCategoryCheckboxAll ?? this.hasCheckboxAll;
        },
        unsortedList() {
            const { indexKey, searchedList, rawList } = this;
            const indexed = R.indexBy(R.prop(indexKey), searchedList);

            return rawList.reduce((acc, listVal) => {
                const key = listVal[indexKey];
                const value = indexed[key];
                if (value) {
                    acc.push(value);
                }

                return acc;
            }, []);
        },
    },
    methods: {
        ...mapActions('iframe', ['postIframeMessage']),
        ...mapMutations('view', ['addNotiMsg']),
        makeSelectedSign(data, disableOnClick) {
            const { hasCheckbox, isItemDisabled } = this;

            if (!hasCheckbox) {
                return null;
            }

            const value = data.selected;
            const disabled = isItemDisabled(data);

            const props = {
                attrs: {
                    value,
                    ...(disableOnClick === true && {
                        disableOnClick,
                    }),
                    ...(disabled && {
                        isBlock: true,
                    }),
                },
            };

            return (
                <div class="item-box-elem__checkbox-wrapper">
                    <CheckBox {...props}></CheckBox>
                </div>
            );
        },
        makeAllChecked: isSubset,
        onClickCheckAll(allChecked, targetList, category = null) {
            // TODO: for GOAL_METRICS
            if (this.widgetType === WIDGET_TYPES.GOAL) {
                this.$emit(
                    'update',
                    undefined,
                    allChecked,
                    targetList,
                    category
                );
                return;
            }

            const list = R.map(
                R.when(
                    R.compose(listIncludes(targetList), R.prop(this.indexKey)),
                    (v) => {
                        return R.assoc(
                            'selected',
                            this.tooltipGenerator(v) ? v.selected : !allChecked,
                            v
                        );
                    }
                ),
                this.currentList
            );

            this.$emit('update', list, allChecked, targetList, category);
        },
        renderList() {
            let {
                renderItem,
                makeAllChecked,
                onClickCheckAll,
                hasCheckbox,
                loopTarget,
                selectedUniqKeys,
                renderItemList,
                hasCheckboxAll,
                currentList,
                isCheckAllDisabled,
                makeTargetList,
            } = this;

            const effectiveList = this.searchText
                ? this.unsortedList
                : currentList;
            const targetList = makeTargetList(effectiveList);

            const hideCheckAll = !hasCheckbox || !hasCheckboxAll;

            const allUniqKeys = hideCheckAll ? [] : targetList;

            const defaultAllChecked = hideCheckAll
                ? false
                : makeAllChecked(selectedUniqKeys, allUniqKeys);
            const allCheckedForAll = hideCheckAll
                ? false
                : (() => {
                      if (typeof this.resolveAllChecked === 'function') {
                          const resolved = this.resolveAllChecked({
                              defaultChecked: defaultAllChecked,
                              selectedUniqKeys,
                              allUniqKeys,
                          });
                          if (typeof resolved === 'boolean') {
                              return resolved;
                          }
                      }
                      return defaultAllChecked;
                  })();

            const showCategoryAll =
                hasCheckbox && hasCheckboxAll && isValueExist(targetList);

            const allCategory = 'all';

            return [
                showCategoryAll && (
                    <ItemList
                        hideCheckAll={hideCheckAll}
                        category={allCategory}
                        checked={allCheckedForAll}
                        useEnhancedList={this.useEnhancedList}
                        onCheckAll={() => {
                            onClickCheckAll(
                                allCheckedForAll,
                                allUniqKeys,
                                allCategory
                            );
                        }}
                        checkAllDisabled={isCheckAllDisabled(allCategory)}
                        currentList={currentList}
                        onUpdate={(...args) => this.$emit('update', ...args)}
                        onUnselectAll={() => this.$emit('unselectAll')}
                        expandOnCheckAllClick={this.expandOnCheckAllClick}
                        unselectAllLabelKey={this.unselectAllLabelKey}
                    />
                ),
                renderItemList({
                    loopTarget,
                    renderer: ({ category, list, offset } = {}) =>
                        renderItem(category, list, offset),
                }),
            ];
        },
        renderContent(params) {
            const {
                makeSelectedSign,
                isItemEditable,
                hasCheckbox,
                additionalIconGenerator,
                disabledOnClick,
                customTextGenerator,
            } = this;
            const {
                data,
                itemKey,
                disabled,
                waypointValue,
                isSelected,
                _header,
                index,
            } = params;

            const { name } = data;

            const $icons = [
                isItemEditable(data) && (
                    <EditIcon
                        v-view-box
                        onClick={(e) => {
                            e.stopPropagation();
                            this.$emit('edit', data);
                        }}
                    />
                ),
                additionalIconGenerator(data),
            ].filter(R.identity);

            return (
                <div
                    data-name={name}
                    data-id={itemKey}
                    data-type="column"
                    class={CLASS_JOIN([
                        'item-box-elem',
                        'table-item',
                        disabled && 'disabled',
                    ])}
                    onClick={() => {
                        if (disabled) {
                            disabledOnClick && disabledOnClick(data);
                        } else {
                            this.$emit('clickCheckBox', data);
                        }
                    }}
                    v-waypoint={waypointValue}
                >
                    {hasCheckbox && makeSelectedSign(data, true)}
                    <div
                        class={`item-text  ${
                            isSelected ? 'selected' : 'unselected'
                        }`}
                    >
                        {isSelected && (
                            <CheckIcon v-view-box class="check-icon" />
                        )}
                        <span
                            class={`item-text-span`}
                            id={`${name}-${index}-span`}
                            {...DIRECTIVE_CLAMP}
                        >
                            {customTextGenerator
                                ? customTextGenerator(data)
                                : _header}
                        </span>

                        {Boolean($icons.length) && (
                            <div class="icons-area">{$icons}</div>
                        )}
                    </div>
                </div>
            );
        },
        renderItem(_, filtered, offset) {
            let {
                translator,
                wrapTooltip,
                isItemDisabled,
                tooltipGenerator,
                additionalTooltipGenerator,
                visibleRange: [start, end] = [0, Infinity],
                handleVisible,
                hasCheckbox,
                translateOr: tOr,
                indexKey,
                renderContent,
            } = this;

            return filtered.map((data, index) => {
                const calcedIndex = offset + index;

                const onScreen = InRange(start, end, calcedIndex);

                const waypointValue = {
                    active: true,
                    callback: (data) =>
                        handleVisible({ data, index: calcedIndex }),
                };
                const itemKey = data[indexKey];
                const { name, selected } = data;

                if (onScreen) {
                    const _header = translator(data);

                    const disabled = isItemDisabled(data);

                    const generatedTooltip =
                        tooltipGenerator(data) ||
                        additionalTooltipGenerator(data);

                    const desc = tOr(
                        `NEW_DASHBOARD.list_header_tooltip_${name}`,
                        ''
                    );

                    const tooltip =
                        desc ||
                        generatedTooltip ||
                        (isCustomMetric(data) && data.description);

                    const isSelected = !hasCheckbox && selected;

                    const contentParams = {
                        data,
                        isSelected,
                        _header,
                        index,
                        waypointValue,
                        disabled,
                        itemKey,
                    };

                    return wrapTooltip({
                        visible: (() => {
                            let visible = Boolean(tooltip);

                            const $el = document.getElementById(
                                `${name}-${index}-span`
                            );

                            if ($el && !visible) {
                                visible = isEllipsisActive($el);
                            }

                            return visible;
                        })(),
                        title: tooltip || _header,
                        key: itemKey,
                        content: renderContent(contentParams),
                    });
                } else {
                    return (
                        <div
                            class="item-box-elem table-item"
                            data-name={name}
                            data-id={itemKey}
                            data-type="column"
                            v-waypoint={waypointValue}
                        />
                    );
                }
            });
        },
        renderItemList({ renderer = R.always(null), closedMap }) {
            const {
                hideCategoryCheckbox,
                onClickCheckAll,
                makeAllChecked,
                selectedUniqKeys,
                hasCheckbox,
                tooltipGenerator,
                effectiveHasCategoryCheckboxAll,
                isCheckAllDisabled,
                makeTargetList,
                categoryNameGenerator,
                itemListCategoryMap: categoryMap,
                loopTarget,
                categoryCheckedResolver,
                categoryIndeterminateResolver,
                emptyCategoryMessage,
                useEnhancedList,
                categoryEditHandler,
                categoryContentRenderer,
            } = this;

            let offset = 0;
            closedMap = closedMap || this.closedMap;
            const selectedUniqKeysSet = new Set(selectedUniqKeys);
            return loopTarget.map(([category]) => {
                const list = categoryMap[category] || [];
                const length = list.length;

                const targetList = makeTargetList(list);
                let resolvedCheckedFromResolver = null;
                let resolvedIndeterminateFromResolver = null;

                if (typeof categoryCheckedResolver === 'function') {
                    const result = categoryCheckedResolver({
                        category,
                        list,
                    });
                    if (typeof result === 'boolean') {
                        resolvedCheckedFromResolver = result;
                    }
                }
                if (typeof categoryIndeterminateResolver === 'function') {
                    const result = categoryIndeterminateResolver({
                        category,
                        list,
                    });
                    if (typeof result === 'boolean') {
                        resolvedIndeterminateFromResolver = result;
                    }
                }

                const hasExplicitCategoryCheckState =
                    typeof resolvedCheckedFromResolver === 'boolean' ||
                    typeof resolvedIndeterminateFromResolver === 'boolean';

                const hideCheckAll =
                    hideCategoryCheckbox ||
                    !hasCheckbox ||
                    !effectiveHasCategoryCheckboxAll ||
                    (!isValueExist(targetList) &&
                        !hasExplicitCategoryCheckState);

                const listNames = hideCheckAll ? [] : targetList;
                const allChecked = hideCheckAll
                    ? false
                    : makeAllChecked(selectedUniqKeys, listNames);
                const selectedCount = hideCheckAll
                    ? 0
                    : listNames.reduce((count, key) => {
                          return selectedUniqKeysSet.has(key)
                              ? count + 1
                              : count;
                      }, 0);
                const hasCategoryIndeterminateResolver =
                    typeof categoryIndeterminateResolver === 'function';
                const isPartiallyChecked = hideCheckAll
                    ? false
                    : hasCategoryIndeterminateResolver &&
                      selectedCount > 0 &&
                      selectedCount < listNames.length;
                let resolvedChecked = allChecked;
                let resolvedIndeterminate = isPartiallyChecked && !allChecked;
                if (typeof resolvedCheckedFromResolver === 'boolean') {
                    resolvedChecked = resolvedCheckedFromResolver;
                }
                if (typeof resolvedIndeterminateFromResolver === 'boolean') {
                    resolvedIndeterminate = resolvedIndeterminateFromResolver;
                }
                const actionChecked = resolvedChecked;

                const firstItem = R.head(list);

                const _isClosed = this.searchText
                    ? this.searchedListClosedMap[category] ?? false
                    : closedMap[category] ?? this.defaultCategoryClosed;

                const isClosed = _isClosed;

                const makeProps = () => {
                    const tooltip = (() => {
                        const allTooltip =
                            typeof tooltipGenerator === 'function' && firstItem
                                ? tooltipGenerator(firstItem)
                                : '';
                        const disabled = isCheckAllDisabled(category, list);

                        return {
                            checkAllTooltip: disabled ? allTooltip : '',
                            checkAllDisabled: disabled,
                        };
                    })();

                    let displayCategory = String(category);
                    if (typeof categoryNameGenerator === 'function') {
                        try {
                            displayCategory = categoryNameGenerator({
                                category,
                                list,
                            });
                        } catch (error) {
                            console.warn(
                                'SecondPanel: categoryNameGenerator failed',
                                error
                            );
                        }
                    }

                    // TODO
                    const attrs = {
                        isClosed,
                        checked: resolvedChecked,
                        indeterminate:
                            resolvedIndeterminate && !resolvedChecked,
                        category,
                        displayCategory,
                        hideCheckAll,
                        ...tooltip,
                        ...{
                            logo: firstItem && firstItem.logo,
                        },
                    };

                    if (typeof categoryEditHandler === 'function') {
                        attrs.categoryEditHandler = categoryEditHandler;
                    }

                    if (typeof categoryContentRenderer === 'function') {
                        attrs.categoryContentRenderer = categoryContentRenderer;
                    }

                    return {
                        props: {
                            ...attrs,
                        },
                        on: {
                            checkAll: () =>
                                onClickCheckAll(
                                    actionChecked,
                                    listNames,
                                    category
                                ),
                            closedChange: (value) => {
                                if (this.searchText) {
                                    this.searchedListClosedMap = R.assoc(
                                        category,
                                        value,
                                        this.searchedListClosedMap
                                    );
                                } else {
                                    this.closedMap = R.assoc(
                                        category,
                                        value,
                                        this.closedMap
                                    );
                                }

                                if (
                                    !value &&
                                    typeof this.handleCategoryToggle ===
                                        'function'
                                ) {
                                    this.handleCategoryToggle({
                                        category,
                                        isClosed: value,
                                        list,
                                    });
                                }
                            },
                        },
                    };
                };

                const emptyMessage =
                    typeof emptyCategoryMessage === 'function'
                        ? emptyCategoryMessage({ category, list })
                        : emptyCategoryMessage;
                const children = length
                    ? renderer({ category, list, offset })
                    : null;

                const ret = (length || this.showEmptyCategories) && (
                    <ItemList
                        {...makeProps()}
                        emptyMessage={emptyMessage}
                        useEnhancedList={useEnhancedList}
                        expandOnCheckAllClick={this.expandOnCheckAllClick}
                    >
                        {children}
                    </ItemList>
                );

                if (!isClosed) {
                    offset += length;
                }

                return ret;
            });
        },
        handleVisible({ data, index }) {
            let { visibles } = this;

            const { going } = data;
            const value = going !== 'out';

            visibles = new Set(visibles);
            if (value) {
                visibles.add(index);
            } else {
                visibles.delete(index);
            }

            this.visibles = visibles;

            this.handleScroll();
        },
        handleScroll: throttle(function () {
            let { visibleCnt, visibles, visibleRange } = this;

            let start = Math.min(...visibles);

            if (start === Infinity) {
                start = 0;
            }

            if (start === visibleRange[0]) {
                return;
            }

            start -= 20;
            let end = start + visibleCnt + 20;

            start = Math.max(start, 0);
            let newValue = [start, end];

            if (end - start < visibleCnt) {
                newValue = [0, Infinity];
            }

            if (notEq(this.visibleRange, newValue)) {
                this.visibleRange = newValue;
            }
        }, 100),
        makeTargetList(list) {
            const { indexKey, tooltipGenerator } = this;

            return R.reduce(
                (acc, item) => {
                    if (tooltipGenerator(item)) {
                        return acc;
                    }

                    acc.push(item[indexKey]);

                    return acc;
                },
                [],
                list
            );
        },
        $genSearchedList() {
            const {
                generateWatchNew$: gen$,
                getCategory,
                categoryNameGenerator,
            } = this;

            const search$ = gen$('searchText').pipe(debounceTime(350));
            const translator$ = gen$('translator').pipe(
                debounceTime(200),
                distinctUntilChanged()
            );

            // const currentList$ = gen$('currentList').pipe(debounceTime(200));
            const currentList$ = gen$('reactiveList').pipe(
                debounceTime(this.reactiveListDebounceMs)
            );

            const obs$ = combineLatest(search$, translator$, currentList$).pipe(
                switchMap(
                    async ([searchText, translator, currentList = []]) => {
                        if (searchText) {
                            return this.$worker
                                .run(
                                    (searchText, currentList) => {
                                        const pattern = new RegExp(
                                            searchText,
                                            'i'
                                        );
                                        return currentList.filter(
                                            (v) =>
                                                pattern.test(
                                                    v.categoryLocale?.replace(
                                                        /\s/g,
                                                        ''
                                                    )
                                                ) ||
                                                pattern.test(
                                                    v.locale?.replace(/\s/g, '')
                                                )
                                        );
                                    },
                                    [
                                        escapeRegExp(
                                            searchText.replace(/\s/g, '')
                                        ),
                                        currentList[0].locale &&
                                        currentList[0].categoryLocale
                                            ? currentList
                                            : currentList.map((d) => {
                                                  const category =
                                                      d.category ??
                                                      getCategory(d);

                                                  const categoryLocale =
                                                      categoryNameGenerator({
                                                          category,
                                                          list: [d],
                                                      });

                                                  return {
                                                      ...d,
                                                      locale:
                                                          translator(d) || '',
                                                      categoryLocale,
                                                  };
                                              }),
                                    ]
                                )
                                .catch((err) => {
                                    console.log(err);
                                });
                        } else {
                            return currentList;
                        }
                    }
                )
            );

            this.$subscribeTo(obs$, (result) => {
                let _finalLst = result;

                if (result.length !== this.reactiveList.length) {
                    const { indexKey } = this;
                    const indexed = R.indexBy(
                        R.prop(indexKey),
                        this.reactiveList
                    );
                    _finalLst = result.map((v) => indexed[v[indexKey]] ?? v);
                }

                this.searchedList = Object.freeze(_finalLst);
            });
        },
        $genItemListCategoryMap() {
            const { generateWatchNew$: gen$ } = this;

            const a$ = gen$('loopTarget');
            const b$ = gen$('filterByCategory');
            const c$ = gen$('unsortedList');

            const combined$ = combineLatest(a$, b$, c$).pipe(
                debounceTime(this.categoryMapDebounceMs)
            );

            this.$subscribeTo(
                combined$,
                ([loopTarget, filterByCategory, unsortedList]) => {
                    const categoryMapGenerator = (loopTarget) => {
                        let _lst = unsortedList;

                        return loopTarget.reduce((acc, [category, items]) => {
                            let [list, rest] = R.partition((data) => {
                                return filterByCategory({
                                    ...data,
                                    itemCategory: data.category,
                                    category,
                                    items,
                                });
                            }, _lst);

                            if (category !== 'bookmark') {
                                _lst = rest;
                            }

                            if (list.length) {
                                acc[category] = list;
                            }

                            return acc;
                        }, {});
                    };

                    this.itemListCategoryMap = Object.freeze(
                        categoryMapGenerator(loopTarget)
                    );
                }
            );
        },
        $applyToReactiveList() {
            const {
                generateWatchNew$: gen$,
                rListUpdateKey,
                rListCompareFn,
            } = this;

            const cList$ = gen$('currentList').pipe(debounceTime(100));

            this.$subscribeTo(cList$, (nextList = []) => {
                const { reactiveList = [] } = this;

                if (reactiveList.length !== nextList.length) {
                    this.reactiveList = nextList.map(
                        R.when(
                            R.propEq('selected', undefined),
                            R.assoc('selected', false)
                        )
                    );
                } else {
                    const { indexKey } = this;

                    const oldIndexed = R.indexBy(
                        R.prop(indexKey),
                        reactiveList
                    );

                    this.reactiveList = nextList.map((value) => {
                        let oldValue = oldIndexed[value[indexKey]];

                        // Fallback for in-session key format changes: if uniqueKey differs but numeric id matches (custom breakdown),
                        // reuse the existing entry so it stays selected/visible.
                        if (!oldValue && Number.isFinite(value.id)) {
                            oldValue = reactiveList.find(
                                (item) =>
                                    Number.isFinite(item.id) &&
                                    item.id === value.id
                            );
                            if (oldValue && value[indexKey]) {
                                oldValue[indexKey] = value[indexKey];
                            }
                        }

                        if (!oldValue) return value;

                        const prevSelected = oldValue.selected;
                        const merged = { ...oldValue, ...value };

                        if (prevSelected !== value.selected) {
                            merged.selected = value.selected;
                        }

                        if (isCustomMetric(merged)) {
                            Object.assign(merged, value);
                        }

                        if (
                            rListUpdateKey &&
                            rListCompareFn(merged) !== rListCompareFn(value)
                        ) {
                            merged[rListUpdateKey] = value[rListUpdateKey];
                        }

                        return merged;
                    });
                }
            });
        },
    },
    created() {
        this.localTranslateOr = memoize(this.translateOr);
    },
    render() {
        const { renderList } = this;
        const isEmptySearch =
            !!this.searchText &&
            !isValueExist(this.makeTargetList(this.unsortedList));

        return (
            <S.MetricListWrapper class="metric-list-wrapper">
                <S.DropdownWrapper emptySearch={isEmptySearch}>
                    {renderList()}
                </S.DropdownWrapper>
            </S.MetricListWrapper>
        );
    },
};

export const ItemList = {
    name: 'ItemList',
    mixins: [iconsMixin],
    props: {
        isClosed: Boolean,
        category: String,
        displayCategory: String,
        checked: Boolean,
        indeterminate: Boolean,
        checkAllDisabled: Boolean,
        checkAllTooltip: String,
        hideCheckAll: {
            type: Boolean,
            default: false,
        },
        useEnhancedList: {
            type: Boolean,
            default: false,
        },
        categoryEditHandler: {
            type: Function,
            default: null,
        },
        categoryContentRenderer: {
            type: Function,
            default: null,
        },
        emptyMessage: {
            type: String,
            default: null,
        },
        logo: String,
        currentList: {
            type: Array,
            default() {
                return [];
            },
        },
        expandOnCheckAllClick: {
            type: Boolean,
            default: true,
        },
        unselectAllLabelKey: {
            type: String,
            default: 'COMMON.unselect_all',
        },
    },
    data() {
        return {
            isShownNotiMsg: false,
        };
    },
    computed: {
        text() {
            const { category, displayCategory } = this;

            return (
                displayCategory ||
                (category === 'all'
                    ? this.$t('COMMON.select_all')
                    : refineRawText(category))
            );
        },
    },
    render() {
        const {
            isClosed,
            category,
            checked,
            indeterminate,
            checkAllTooltip,
            checkAllDisabled,
            hideCheckAll,
            text,
            logo,
            currentList,
            useEnhancedList,
            categoryEditHandler,
            categoryContentRenderer,
            expandOnCheckAllClick,
        } = this;
        const isAll = category === 'all';
        const handleCheckAllAndExpand = (e) => {
            e && e.stopPropagation && e.stopPropagation();
            if (checkAllDisabled) {
                return;
            }

            this.$emit('checkAll');
            if (expandOnCheckAllClick && !isAll && isClosed) {
                this.$emit('closedChange', false);
            }
        };
        const emitUnselectAll = () => {
            const list = Array.isArray(currentList)
                ? currentList.map(R.assoc('selected', false))
                : [];
            const targetEntryKeys = Array.isArray(currentList)
                ? currentList.map(({ entryKey }) => entryKey)
                : [];

            this.$emit('unselectAll');
            this.$emit('update', list, true, targetEntryKeys);
        };

        if (!useEnhancedList) {
            return isAll && hideCheckAll ? null : (
                <S.ItemListWrpper
                    class="item-list-wrapper"
                    data-isClose={String(isClosed)}
                    data-category={category}
                >
                    <S.TitleLabel isClosed={isAll ? true : isClosed}>
                        {!hideCheckAll && (
                            <S.ItemCheckWrapper
                                onClick={handleCheckAllAndExpand}
                            >
                                <CheckBox
                                    value={checked}
                                    indeterminate={indeterminate}
                                    tooltip={checkAllTooltip}
                                    isBlock={checkAllDisabled}
                                />
                            </S.ItemCheckWrapper>
                        )}
                        <S.ItemTextWrapper
                            onClick={() => {
                                this.$emit('closedChange', !isClosed);
                            }}
                        >
                            {logo ? (
                                <S.Logo src={logo} />
                            ) : (
                                this.channelIconWithAdverity(category)
                            )}
                            <span class="vue-line-clamp">{text}</span>
                        </S.ItemTextWrapper>
                        {isAll ? (
                            <div
                                onClick={emitUnselectAll}
                                style="cursor:pointer;"
                            >
                                {this.$t(this.unselectAllLabelKey)}
                            </div>
                        ) : (
                            <Accordion
                                v-view-box
                                class="accordion"
                                onClick={() => {
                                    this.$emit('closedChange', !isClosed);
                                }}
                            />
                        )}
                    </S.TitleLabel>
                    <S.ItemWrapper isClosed={isAll ? true : isClosed}>
                        {isClosed === false && this.$slots.default}
                    </S.ItemWrapper>
                </S.ItemListWrpper>
            );
        }

        const emptyMessage = this.emptyMessage;
        const slotItems = this.$slots.default;
        const normalizedSlotItems = Array.isArray(slotItems)
            ? slotItems.filter((node) => {
                  if (!node || node.isComment) {
                      return false;
                  }
                  if (typeof node.text === 'string') {
                      return node.text.trim().length > 0;
                  }
                  return Boolean(node.tag);
              })
            : slotItems;
        const hasChildren = Array.isArray(normalizedSlotItems)
            ? normalizedSlotItems.length > 0
            : Boolean(normalizedSlotItems);
        const shouldRenderItemWrapper =
            isClosed || hasChildren || Boolean(emptyMessage);
        const canEditCategory = typeof categoryEditHandler === 'function';
        const renderCategoryIcon = () =>
            logo ? (
                <S.Logo src={logo} />
            ) : (
                this.channelIconWithAdverity(category)
            );
        const renderCategoryContent = () => {
            if (typeof categoryContentRenderer === 'function') {
                return categoryContentRenderer({
                    category,
                    text,
                    logo,
                    renderCategoryIcon,
                });
            }

            return [
                renderCategoryIcon(),
                <span class="vue-line-clamp">{text}</span>,
            ];
        };

        return isAll && hideCheckAll ? null : (
            <S.ItemListWrpper
                class="item-list-wrapper"
                data-isClose={String(isClosed)}
                data-category={category}
            >
                <S.TitleLabel isClosed={isAll ? true : isClosed}>
                    {!hideCheckAll && (
                        <S.ItemCheckWrapper onClick={handleCheckAllAndExpand}>
                            <CheckBox
                                value={checked}
                                indeterminate={indeterminate}
                                tooltip={checkAllTooltip}
                                isBlock={checkAllDisabled}
                            />
                        </S.ItemCheckWrapper>
                    )}
                    <S.ItemTextWrapper
                        onClick={() => {
                            this.$emit('closedChange', !isClosed);
                        }}
                    >
                        {renderCategoryContent()}
                    </S.ItemTextWrapper>
                    {isAll ? (
                        <div onClick={emitUnselectAll} style="cursor:pointer;">
                            {this.$t(this.unselectAllLabelKey)}
                        </div>
                    ) : (
                        <div class="item-list-actions">
                            {canEditCategory && (
                                <EditIcon
                                    v-view-box
                                    class="category-edit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        categoryEditHandler &&
                                            categoryEditHandler({ category });
                                    }}
                                />
                            )}
                            <Accordion
                                v-view-box
                                class="accordion"
                                onClick={() => {
                                    this.$emit('closedChange', !isClosed);
                                }}
                            />
                        </div>
                    )}
                </S.TitleLabel>
                {shouldRenderItemWrapper && (
                    <S.ItemWrapper isClosed={isAll ? true : isClosed}>
                        {isClosed === false &&
                            (hasChildren
                                ? normalizedSlotItems
                                : emptyMessage && (
                                      <S.EmptyCategoryRow>
                                          {emptyMessage}
                                      </S.EmptyCategoryRow>
                                  ))}
                    </S.ItemWrapper>
                )}
            </S.ItemListWrpper>
        );
    },
};
